import React, { useState, useEffect } from 'react';
import { Button, Box, Typography, CircularProgress } from '@mui/material';
import { Google } from '@mui/icons-material';
import googleOAuthService from '../services/googleOAuthService';
import { useAuth } from '../contexts/AuthContext';

const GoogleSignInButton = ({ onSuccess, onError, disabled = false }) => {
  const [loading, setLoading] = useState(false);
  const [isAvailable, setIsAvailable] = useState(false);
  const { login } = useAuth();

  useEffect(() => {
    // Initialize Google OAuth service
    const initializeGoogle = async () => {
      try {
        await googleOAuthService.initialize();
        // Wait a bit for the Google script to fully load
        setTimeout(() => {
          setIsAvailable(googleOAuthService.isAvailable());
        }, 100);
      } catch (error) {
        console.error('Failed to initialize Google OAuth:', error);
        setIsAvailable(false);
      }
    };

    initializeGoogle();

    // Cleanup on unmount
    return () => {
      googleOAuthService.cleanup();
    };
  }, []);

  const handleGoogleSignIn = async () => {
    if (disabled || loading) return;

    setLoading(true);

    try {
      await googleOAuthService.signIn(
        async (backendResponse) => {
          try {
            // The Google service already processed the login with backend
            // Now we need to update the auth context with the received data
            console.log('Google login successful, updating auth context:', backendResponse);
            
            // Store the tokens in localStorage
            if (backendResponse.token) {
              localStorage.setItem('token', backendResponse.token);
            }
            if (backendResponse.refreshToken) {
              localStorage.setItem('refreshToken', backendResponse.refreshToken);
            }
            
            // Update the auth context using the login method
            await login({
              user: backendResponse.user,
              token: backendResponse.token,
              refreshToken: backendResponse.refreshToken
            });

            if (onSuccess) {
              onSuccess(backendResponse);
            }
          } catch (error) {
            console.error('Error updating auth context:', error);
            if (onError) {
              onError(error);
            }
          } finally {
            setLoading(false);
          }
        },
        (error) => {
          setLoading(false);
          console.error('Google Sign-In error:', error);
          if (onError) {
            onError(error);
          }
        }
      );
    } catch (error) {
      setLoading(false);
      console.error('Google Sign-In error:', error);
      if (onError) {
        onError(error);
      }
    }
  };

  // Don't render if Google OAuth is not available
  if (!isAvailable) {
    return null;
  }

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      <Typography 
        variant="body2" 
        align="center" 
        sx={{ mb: 1, color: 'text.secondary' }}
      >
        or
      </Typography>
      
      <Button
        fullWidth
        variant="outlined"
        size="large"
        onClick={handleGoogleSignIn}
        disabled={disabled || loading}
        startIcon={
          loading ? (
            <CircularProgress size={20} />
          ) : (
            <Google sx={{ color: '#4285F4' }} />
          )
        }
        sx={{
          py: 1.5,
          borderColor: '#dadce0',
          color: '#3c4043',
          textTransform: 'none',
          fontSize: '14px',
          fontWeight: 500,
          '&:hover': {
            borderColor: '#dadce0',
            backgroundColor: '#f8f9fa',
          },
          '&:disabled': {
            borderColor: '#dadce0',
            color: '#3c4043',
            opacity: 0.6,
          }
        }}
      >
        {loading ? 'Signing in...' : 'Continue with Google'}
      </Button>
    </Box>
  );
};

export default GoogleSignInButton;
